import { Component, createRef } from 'react';

import { GenericMouseEvent } from '../../../types/types';
import { UIUtils } from '../../../utils/dom-utils';
import { JSUtils } from '../../../utils/js.utils';
import { WindowUtils } from '../../../utils/window.utils';
import style from './range-selector.style.module.scss';
import { OptionalChildrenProps } from '../../../types/react.types';


interface RangeSelectorProps extends OptionalChildrenProps {
    max?: number;
    min?: number;
    start?: number;
    end?: number;
    step?: number;
    valueFormatter?: (value: any) => string;
    onChange?: (value: RangeSelectorResult) => void;
    onChangeEnd?: (value: RangeSelectorResult) => void;
}

interface RangeSelectorState {
    start: number;
    end: number;
    left: number;
    right: number;
}

export type RangeHandlerType = 'start' | 'end';
export class RangeHandlerTypes {
    public static START: RangeHandlerType = 'start';
    public static END: RangeHandlerType = 'end';
}


export interface RangeSelectorResult {
    handlerType: RangeHandlerType;
    start: number;
    end: number;
}

export default class RangeSelector extends Component<RangeSelectorProps, RangeSelectorState> {
    container = createRef<HTMLDivElement>();
    handler: ((e: GenericMouseEvent) => void) | null = null;
    eventOffset = 0; // Need to set this to 0 if manually uploading

    startHandler: HTMLElement | null = null;
    endHandler: HTMLElement | null = null;
    isStartHandler: boolean = false;

    max = 100;
    min = 0;
    step = 1;

    valueFormatter = (val: number) => val;

    readonly state: Readonly<RangeSelectorState> = {
        start: this.props?.start ?? 0,
        end: this.props?.end ?? 100,
        left: this.props?.start ?? 0,
        right: this.props?.end ?? 100,
    };
    constructor(props: RangeSelectorProps) {
        super(props);
        this.max = this.props.max || 100;
        this.min = this.props.min || 0;
        this.step = this.props.step || 1;

        const stepStr = `${this.step}`;
        if (stepStr.indexOf('.') > -1) {
            const numberOfDecimals = stepStr.length - (stepStr.indexOf('.') + 1);
            this.valueFormatter = (val) => {
                const parsedVal = val.toFixed(numberOfDecimals);
                return Number(parsedVal);
            };
        }
    }

    componentDidMount() {
        JSUtils.runMacroTask(() => {
            if (!this.container.current) return;
            this.startHandler = this.container.current.querySelector(`.${style.start}`) as HTMLElement;
            this.endHandler = this.container.current.querySelector(`.${style.end}`) as HTMLElement;

            const rail: HTMLElement = this.container.current.querySelector(`.${style.rail}`) as HTMLElement;
            const railRect = rail.getBoundingClientRect();
            const THUMB_WIDTH = 18;
            const availableWidth = railRect.width - THUMB_WIDTH;

            this.isStartHandler = false;
            const endP = ((this.props.end ?? this.max) - this.min) / (this.max - this.min);
            const initialEndPageX = railRect.left + (endP * availableWidth);
            this.updateHandlersPositionAndNotify(initialEndPageX, false);

            this.isStartHandler = true;
            const startP = ((this.props.start ?? this.min) - this.min) / (this.max - this.min);
            const initialStartPageX = railRect.left + (startP * availableWidth);
            this.updateHandlersPositionAndNotify(initialStartPageX, false);
            this.forceUpdate();
        });
    }

    handleMousedown(e: MouseEvent | any) {
        if (e.target instanceof HTMLElement) {
            this.startHandler = this.container.current.querySelector(`.${style.start}`) as HTMLElement;
            this.endHandler = this.container.current.querySelector(`.${style.end}`) as HTMLElement;
            this.isStartHandler = e.target.classList.contains(`${style.start}`);
            this.handler = (ev: any) => this.handleMouseMove(ev);
            const event = UIUtils.toGenericMouseEvent(e);
            if (event) {
                this.eventOffset = event.offsetX;
                WindowUtils.addMoveMoveHandlerToWindow(this.handler, true);
            }
        }
    }

    handleMouseMove(event: GenericMouseEvent) {
        if (event.type === 'touchend' || event.type === 'mouseup') {
            this.handler = null;
            return;
        }
        if (event) {
            this.updateHandlersPositionAndNotify(event.pageX);
        }
    }

    private updateHandlersPositionAndNotify(pageX: number, notify = true) {
        if (!this.startHandler || !this.endHandler) {
            return;
        }

        const THUMB_WIDTH = 18;
        const HALF_THUMB = THUMB_WIDTH / 2;

        const rail: HTMLElement = this.container.current.querySelector(`.${style.rail}`) as HTMLElement;
        const railRect = rail.getBoundingClientRect();
        const availableWidth = railRect.width - THUMB_WIDTH;

        let handlerLeft = pageX - (railRect.left + this.eventOffset);

        // Map handlerLeft (which is relative to rail start) to the visual range [0, availableWidth]
        // But wait, handlerLeft assumes click on thumb. handlerLeft is intended new 'left' style pixel value?
        // Yes, roughly.
        // We need to constrain handlerLeft to [0, availableWidth].

        // Correct for eventOffset being inside the thumb? 
        // Logic: pageX is mouse. eventOffset is mouse inside thumb.
        // railRect.left is rail start.
        // Proposed Left = Mouse - RailStart - Offset.
        // This calculates where the LEFT EDGE of the thumb should be. Correct.

        if (handlerLeft < 0) handlerLeft = 0;
        if (handlerLeft > availableWidth) handlerLeft = availableWidth;

        // Current positions in px for collision detection
        const currentStartLeft = parseFloat(this.startHandler.style.left) / 100 * railRect.width || 0; // Approximation if % used?
        // Better to recover P from value?
        // Let's rely on values for collision? No, dragging feel needs immediate feedback.

        // Let's compute normalized P (0..1)
        const p = handlerLeft / availableWidth;
        const readablePosition = p * (this.max - this.min) + this.min;

        // Step quantization
        const step = this.step;
        const remainder = (readablePosition - this.min) % step;
        let quantizedValue = readablePosition - remainder;
        if (remainder >= step / 2) quantizedValue += step;

        // Constrain value
        if (quantizedValue < this.min) quantizedValue = this.min;
        if (quantizedValue > this.max) quantizedValue = this.max;

        // Re-calculate P from quantized value for snapping visual?
        // Usually better to snap.
        const quantizedP = (quantizedValue - this.min) / (this.max - this.min);

        // Check collision (value based)
        if (this.isStartHandler) {
            if (quantizedValue >= this.state.end) {
                quantizedValue = this.state.end - step;
            }
        } else {
            if (quantizedValue <= this.state.start) {
                quantizedValue = this.state.start + step;
            }
        }

        // Recalculate P after collision check
        const finalP = (quantizedValue - this.min) / (this.max - this.min);

        const position = this.valueFormatter(quantizedValue);

        // Style Logic: left = calc(P% - P*18px) -> No wait, calc(P*100% - P*18px)
        // Actually simpler: left = calc(P * (100% - 18px))
        // This matches 0->0 and 1->100%-18px exactly.
        const leftStyle = `calc(${finalP * 100}% - ${finalP * THUMB_WIDTH}px)`;

        if (this.isStartHandler) {
            this.startHandler.style.left = leftStyle;
            this.setState({
                start: position,
                left: finalP,
            });
        } else {
            this.endHandler.style.left = leftStyle;
            this.setState({
                end: position,
                right: finalP,
            });
        }

        const bridgeEl = this.container.current.querySelector<HTMLDivElement>(`.${style.bridge}`);
        if (bridgeEl) {
            // Bridge from center to center? Or inner edge?
            // Let's use logic: StartHandle Left + 9px to EndHandle Left + 9px.
            // StartHandle Left = startP * (100% - 18px)
            // Left = calc(StartStyleLeft + 9px)
            // Right = calc(100% - (EndStyleLeft + 9px))

            // Getting current P from state or just calculated?
            // Need both Ps.
            const startP = this.isStartHandler ? finalP : (this.state.start - this.min) / (this.max - this.min);
            const endP = !this.isStartHandler ? finalP : (this.state.end - this.min) / (this.max - this.min);

            bridgeEl.style.left = `calc(${startP * 100}% - ${startP * THUMB_WIDTH}px + ${HALF_THUMB}px)`;
            bridgeEl.style.right = `calc(100% - (${endP * 100}% - ${endP * THUMB_WIDTH}px + ${HALF_THUMB}px))`;
        }


        if (this.props.onChange && notify) {
            this.props.onChange({
                handlerType: this.isStartHandler ? RangeHandlerTypes.START : RangeHandlerTypes.END,
                start: this.isStartHandler ? position : this.state.start,
                end: this.isStartHandler ? this.state.end : position,
            });
        }
        if (this.props.onChangeEnd && notify) {
            this.props.onChangeEnd({
                handlerType: this.isStartHandler ? RangeHandlerTypes.START : RangeHandlerTypes.END,
                start: this.isStartHandler ? position : this.state.start,
                end: this.isStartHandler ? this.state.end : position,
            });
        }
    }

    componentWillUpdate(next) {
        if (!this.handler) {
            this.eventOffset = 0;
            if (this.props.start !== next.start || this.props.end !== next.end) {
                console.error('React migrate');
                // this.props.start = next.start;
                // this.props.end = next.end;
                this.componentDidMount();
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const shouldRenderProps = !this.handler && (this.props.start !== nextProps.start || this.props.end !== nextProps.end);
        const shouldRenderState = nextState && nextState.start !== this.state.start || nextState.end !== this.state.end
        return shouldRenderProps || shouldRenderState;
    }

    render() {
        return (
            <div ref={this.container} className={style.container}>
                <div className={style.state}>
                    <div className={style.min}>{this.state.start}</div>
                    <div className={style.value}> </div>
                    <div className={style.max}>{this.state.end}</div>
                </div>
                <div className={style['range-selector']}>
                    <div className={style.rail}>
                        <span className={style.bridge}></span>
                        <span className={`${style.handler} ${style.start}`}
                            onTouchStart={(e) => this.handleMousedown(e)}
                            onMouseDown={(e) => this.handleMousedown(e)}
                        ></span>
                        <span className={`${style.handler} ${style.end}`}
                            onTouchStart={(e) => this.handleMousedown(e)}
                            onMouseDown={(e) => this.handleMousedown(e)}
                        ></span>
                    </div>
                </div>
            </div>
        );
    }
}